import { FunctionComponent } from 'react';
import { DashboardWrapper } from 'src/shared';
import { Card, Text } from 'src/shared';
import styled from 'styled-components';
import { Colors } from 'src/utils/color';
import { Size } from 'src/shared/text';

// Mock data for patient details
const mockPatientDetail = {
  id: '1',
  firstName: 'Emily',
  lastName: 'Johnson',
  dateOfBirth: '1975-03-15',
  gender: 'Female',
  medicalRecordNumber: 'MRN-2024-001',
  overallRiskScore: 25,
  sessionsCount: 8,
  sessions: [
    {
      id: '1',
      date: '2024-01-15T09:30:00Z',
      deviceType: 'Heidelberg Spectralis OCT',
      status: 'completed',
      riskScore: 25,
      metrics: {
        stabilityScore: 85.2,
        fixationCount: 127,
        averageFixationDuration: 180,
        saccadeCount: 45,
        diabeticRetinopathyRisk: 15.2,
        macularDegenerationRisk: 8.7,
        glaucomaRisk: 12.3
      }
    },
    {
      id: '2',
      date: '2024-01-01T14:20:00Z',
      deviceType: 'Zeiss Cirrus HD-OCT',
      status: 'completed',
      riskScore: 28,
      metrics: {
        stabilityScore: 82.1,
        fixationCount: 134,
        averageFixationDuration: 175,
        saccadeCount: 48,
        diabeticRetinopathyRisk: 16.8,
        macularDegenerationRisk: 9.2,
        glaucomaRisk: 13.1
      }
    }
  ]
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const PatientInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Avatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${Colors.theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  font-weight: 600;
`;

const PatientDetails = styled.div`
  flex: 1;
`;

const PatientName = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 4px;
`;

const PatientMeta = styled.div`
  font-size: 14px;
  color: ${Colors.extra.dark_gray};
  margin-bottom: 8px;
`;

const RiskBadge = styled.div<{ risk: 'low' | 'medium' | 'high' }>`
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  background-color: ${props => 
    props.risk === 'high' ? '#fee2e2' : 
    props.risk === 'medium' ? '#fef3c7' : '#d1fae5'
  };
  color: ${props => 
    props.risk === 'high' ? '#dc2626' : 
    props.risk === 'medium' ? '#d97706' : '#059669'
  };
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const MetricCard = styled(Card)`
  padding: 20px;
  text-align: center;
`;

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 600;
  color: ${Colors.theme.primary};
  margin-bottom: 8px;
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: ${Colors.extra.dark_gray};
  margin-bottom: 4px;
`;

const MetricSubtitle = styled.div`
  font-size: 12px;
  color: ${Colors.extra.light_gray};
`;

const SessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SessionCard = styled(Card)`
  padding: 20px;
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const SessionInfo = styled.div`
  flex: 1;
`;

const SessionDate = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 4px;
`;

const SessionDevice = styled.div`
  font-size: 14px;
  color: ${Colors.extra.dark_gray};
`;

const SessionMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const SessionMetric = styled.div`
  text-align: center;
`;

const SessionMetricValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${Colors.extra.black};
  margin-bottom: 4px;
`;

const SessionMetricLabel = styled.div`
  font-size: 12px;
  color: ${Colors.extra.dark_gray};
`;

const RiskScoreBar = styled.div`
  margin-top: 16px;
`;

const RiskScoreHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const RiskScoreBarBg = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${Colors.extra.light_gray};
  border-radius: 4px;
  overflow: hidden;
`;

const RiskScoreBarFill = styled.div<{ score: number }>`
  height: 100%;
  width: ${props => Math.min(100, props.score)}%;
  background-color: ${props => 
    props.score >= 70 ? '#dc2626' : 
    props.score >= 40 ? '#d97706' : '#059669'
  };
  transition: width 0.3s ease;
`;

const PatientDetailPage: FunctionComponent = () => {

  const getRiskLevel = (score: number): 'low' | 'medium' | 'high' => {
    if (score >= 70) return 'high';
    if (score >= 40) return 'medium';
    return 'low';
  };

  const getRiskLabel = (score: number): string => {
    if (score >= 70) return 'High Risk';
    if (score >= 40) return 'Medium Risk';
    return 'Low Risk';
  };

  const patient = mockPatientDetail;
  const initials = `${patient.firstName.charAt(0)}${patient.lastName.charAt(0)}`;
  const age = new Date().getFullYear() - new Date(patient.dateOfBirth).getFullYear();

  return (
    <DashboardWrapper>
      <Container>
        <Header>
          <PatientInfo>
            <Avatar>{initials}</Avatar>
            <PatientDetails>
              <PatientName>{patient.firstName} {patient.lastName}</PatientName>
              <PatientMeta>
                {age}y, {patient.gender} • MRN: {patient.medicalRecordNumber}
              </PatientMeta>
              <PatientMeta>
                {patient.sessionsCount} sessions • Last visit: {patient.sessions[0] ? new Date(patient.sessions[0].date).toLocaleDateString() : 'Never'}
              </PatientMeta>
            </PatientDetails>
          </PatientInfo>
          <RiskBadge risk={getRiskLevel(patient.overallRiskScore)}>
            {getRiskLabel(patient.overallRiskScore)}
          </RiskBadge>
        </Header>

        {/* Key Metrics */}
        <MetricsGrid>
          <MetricCard>
            <MetricValue>{patient.overallRiskScore}%</MetricValue>
            <MetricLabel>Overall Risk Score</MetricLabel>
            <MetricSubtitle>Current assessment</MetricSubtitle>
          </MetricCard>
          <MetricCard>
            <MetricValue>{patient.sessionsCount}</MetricValue>
            <MetricLabel>Total Sessions</MetricLabel>
            <MetricSubtitle>All time</MetricSubtitle>
          </MetricCard>
          <MetricCard>
            <MetricValue>{patient.sessions[0]?.metrics.stabilityScore || 0}%</MetricValue>
            <MetricLabel>Fixation Stability</MetricLabel>
            <MetricSubtitle>Latest session</MetricSubtitle>
          </MetricCard>
          <MetricCard>
            <MetricValue>{patient.sessions[0]?.metrics.fixationCount || 0}</MetricValue>
            <MetricLabel>Fixation Count</MetricLabel>
            <MetricSubtitle>Latest session</MetricSubtitle>
          </MetricCard>
        </MetricsGrid>

        {/* Sessions */}
        <div>
          <Text fontSize={Size.Medium} fontWeight="600" marginBottom={16}>
            Session History
          </Text>
          <SessionsList>
            {patient.sessions.map((session) => (
              <SessionCard key={session.id}>
                <SessionHeader>
                  <SessionInfo>
                    <SessionDate>
                      {new Date(session.date).toLocaleDateString()} at {new Date(session.date).toLocaleTimeString()}
                    </SessionDate>
                    <SessionDevice>{session.deviceType}</SessionDevice>
                  </SessionInfo>
                  <RiskBadge risk={getRiskLevel(session.riskScore)}>
                    {getRiskLabel(session.riskScore)}
                  </RiskBadge>
                </SessionHeader>

                <SessionMetrics>
                  <SessionMetric>
                    <SessionMetricValue>{session.metrics.stabilityScore}%</SessionMetricValue>
                    <SessionMetricLabel>Stability Score</SessionMetricLabel>
                  </SessionMetric>
                  <SessionMetric>
                    <SessionMetricValue>{session.metrics.fixationCount}</SessionMetricValue>
                    <SessionMetricLabel>Fixation Count</SessionMetricLabel>
                  </SessionMetric>
                  <SessionMetric>
                    <SessionMetricValue>{session.metrics.averageFixationDuration}ms</SessionMetricValue>
                    <SessionMetricLabel>Avg Fixation Duration</SessionMetricLabel>
                  </SessionMetric>
                  <SessionMetric>
                    <SessionMetricValue>{session.metrics.saccadeCount}</SessionMetricValue>
                    <SessionMetricLabel>Saccade Count</SessionMetricLabel>
                  </SessionMetric>
                </SessionMetrics>

                <RiskScoreBar>
                  <RiskScoreHeader>
                    <Text fontSize={Size.XSmall} color={Colors.extra.dark_gray}>Risk Score</Text>
                    <Text fontSize={Size.XSmall} fontWeight="600">{Math.round(session.riskScore)}%</Text>
                  </RiskScoreHeader>
                  <RiskScoreBarBg>
                    <RiskScoreBarFill score={session.riskScore} />
                  </RiskScoreBarBg>
                </RiskScoreBar>
              </SessionCard>
            ))}
          </SessionsList>
        </div>
      </Container>
    </DashboardWrapper>
  );
};

export default PatientDetailPage;
